<template>
  <div class="rv-gen-report">
    <!-- Date Range & Generate Button -->
    <div class="rv-gen-report__controls">
      <div class="rv-gen-report__date-row">
        <div class="rv-gen-report__date-col">
          <label class="rv-gen-report__label">Date Range</label>
          <v-menu
            ref="menu1"
            v-model="menu1"
            :close-on-content-click="false"
            :return-value.sync="date"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <div
                class="rv-datepicker__trigger"
                :class="{ 'rv-datepicker__trigger--error': generateReportError.dateRange }"
                v-bind="attrs"
                v-on="on"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="rv-datepicker__icon">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
                </svg>
                <span v-if="searchRptDate" class="rv-datepicker__text">{{ searchRptDate }}</span>
                <span v-else class="rv-datepicker__placeholder">Select date range...</span>
              </div>
            </template>
            <v-date-picker color="var(--app-accent)"
              range
              v-model="generateReportData.searchRptDateRange"
              no-title
              scrollable
            >
              <v-spacer></v-spacer>
              <v-btn text color="primary" @click="menu1 = false"> Cancel </v-btn>
              <v-btn text color="primary" @click="$refs.menu1.save(date)">
                OK
              </v-btn>
            </v-date-picker>
          </v-menu>
          <div v-if="generateReportError.dateRange" class="rv-gen-report__error">{{ generateReportError.dateRange }}</div>
        </div>

        <div class="rv-gen-report__action-col">
          <button class="rv-gen-report__generate-btn" @click="generateReport()">
            <v-icon small color="#fff" class="mr-2">mdi-file-document-refresh</v-icon>
            Generate
          </button>
        </div>
      </div>
    </div>

    <!-- Report Summary -->
    <div v-if="showGenerateReport" class="rv-gen-report__summary">
      <!-- Summary Header -->
      <div class="rv-gen-report__summary-header">
        <div>
          <h2 class="rv-gen-report__summary-title">HRDC Report - taxPOD</h2>
          <span class="rv-gen-report__summary-week">WEEK {{ generateReportData.week }}</span>
        </div>
        <button class="rv-gen-report__download-btn" @click="downloadReport()">
          <v-icon small color="var(--app-accent)" class="mr-1">mdi-download-circle</v-icon>
          Download Report
        </button>
      </div>

      <!-- General Info Section -->
      <div class="rv-gen-report__section">
        <div class="rv-gen-report__section-header">
          <v-icon small color="#fff" class="mr-2">mdi-information-outline</v-icon>
          General Information
        </div>
        <div class="rv-gen-report__section-body">
          <table class="rv-gen-table">
            <thead>
              <tr>
                <th width="8%">No.</th>
                <th width="72%">Item</th>
                <th width="20%">Quantity</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(info, key1, index) in generateReportData.generalInfo"
                :key="index + key1"
              >
                <td class="rv-gen-table__num">{{ index + 1 }}</td>
                <td>{{ reportDesc[key1] }}</td>
                <td v-if="key1 == 'transFrHrdcToYyc'" class="rv-gen-table__qty">
                  <input v-model="transFrHrdcToYyc" class="rv-gen-table__input" />
                </td>
                <td v-else v-html="info" class="rv-gen-table__qty"></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- To Date Label -->
      <div class="rv-gen-report__todate-label">
        TO DATE - {{ generateReportData.weekEndDate }}
      </div>

      <!-- Ongoing Applications Section -->
      <div class="rv-gen-report__section">
        <div class="rv-gen-report__section-header">
          <v-icon small color="#fff" class="mr-2">mdi-progress-clock</v-icon>
          Ongoing Applications
        </div>
        <div class="rv-gen-report__section-body">
          <table class="rv-gen-table">
            <thead>
              <tr>
                <th width="8%">No.</th>
                <th width="72%">Item</th>
                <th width="20%">Quantity</th>
              </tr>
            </thead>
            <tbody>
              <tr class="rv-gen-table__row--bold">
                <td class="rv-gen-table__num">1</td>
                <td>
                  Ongoing Applications (including up to Claim Approved stage)
                </td>
                <td class="rv-gen-table__qty">{{ ongoingApplicationSum }}</td>
              </tr>
              <tr
                v-for="(
                  info, key2, index
                ) in generateReportData.ongoingApplications"
                :key="index + key2"
              >
                <td class="rv-gen-table__num"></td>
                <td class="rv-gen-table__sub-item">{{ ongoingApplications[key2] }}</td>
                <td v-if="key2 == 'pendingReqOfHrdcInvoice'" class="rv-gen-table__qty">
                  <input type="number" v-model="pendingReqOfHrdcInvoice" class="rv-gen-table__input" />
                </td>
                <td :style="getBackgroundColor(key2)" v-else class="rv-gen-table__qty">{{ info }}</td>
              </tr>
              <tr v-for="i in 2" :key="i + 'ongoingApplications'">
                <td>&nbsp;</td>
                <td>&nbsp;</td>
                <td>&nbsp;</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Pending Refunds Section -->
      <div class="rv-gen-report__section">
        <div class="rv-gen-report__section-header">
          <v-icon small color="#fff" class="mr-2">mdi-cash-refund</v-icon>
          Pending Refunds
        </div>
        <div class="rv-gen-report__section-body">
          <table class="rv-gen-table">
            <thead>
              <tr>
                <th width="8%">No.</th>
                <th width="72%">Item</th>
                <th width="20%">Quantity</th>
              </tr>
            </thead>
            <tbody>
              <tr class="rv-gen-table__row--bold">
                <td class="rv-gen-table__num">2</td>
                <td>Pending Refunds</td>
                <td class="rv-gen-table__qty">{{ pendingRefundSum }}</td>
              </tr>
              <tr
                v-for="(info2, key3, index2) in generateReportData.pendingRefunds"
                :key="index2 + key3"
              >
                <td class="rv-gen-table__num"></td>
                <td class="rv-gen-table__sub-item">{{ pendingRefunds[key3] }}</td>
                <td :style="getBackgroundColor(key3)" class="rv-gen-table__qty">{{ info2 }}</td>
              </tr>
              <tr v-for="j in 1" :key="j + 'pendingRefunds'">
                <td>&nbsp;</td>
                <td>&nbsp;</td>
                <td>&nbsp;</td>
              </tr>
              <tr class="rv-gen-table__row--bold">
                <td class="rv-gen-table__num">3</td>
                <td>Pending taxPOD Account Enrollments</td>
                <td class="rv-gen-table__qty">{{ generateReportData.pendingAccountEnrollment }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  computed: {
    searchRptDate() {
      if (this.generateReportData.searchRptDateRange) {
        return this.generateReportData.searchRptDateRange.join(" - ");
      }

      return null;
    },
    ongoingApplicationSum() {
      let sum = 0;
      for (const key in this.generateReportData.ongoingApplications) {
        if (
          typeof this.generateReportData.ongoingApplications[key] ===
            "number" &&
          !isNaN(this.generateReportData.ongoingApplications[key])
        ) {
          sum += this.generateReportData.ongoingApplications[key];
        }
      }

      if (this.pendingReqOfHrdcInvoice) {
        sum += Number(this.pendingReqOfHrdcInvoice);
      }

      return sum;
    },
    pendingRefundSum() {
      let sum = 0;
      for (const key in this.generateReportData.pendingRefunds) {
        if (
          typeof this.generateReportData.pendingRefunds[key] === "number" &&
          !isNaN(this.generateReportData.pendingRefunds[key])
        ) {
          sum += this.generateReportData.pendingRefunds[key];
        }
      }

      return sum;
    },
  },
  emits: ["generateReport", "downloadReport"],
  props: {
    generateReportData: Object,
    generateReportError: Object,
    showGenerateReport: Boolean,
    notReadyReportData: Array,
  },
  data: () => ({
    date: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
      .toISOString()
      .substr(0, 10),
    menu1: false,
    transFrHrdcToYyc: null,
    pendingReqOfHrdcInvoice: null,
    reportDesc: {
      newApplications: "New Applications",
      resendApplications: "Resend Applications",
      grantsApproved: "Grant Approved",
      claimsSubmitted: "Claims Submitted",
      claimsApproved: "Claims Approved",
      transFrHrdcToYyc: "Transaction From HRDC to YYC",
    },
    ongoingApplications: {
      pendingGrantsApplicationOrApproval:
        "Pending Grants Application or Approval",
      grantsApprovedPendingForm:
        "Grant Approved, Pending Client to Revert Completed JD14 and T3 forms",
      pendingGrantsWithIssue: "Pending Grants with Issues",
      pendingSubscriptionEndToReqInvoice:
        "Pending End of Date of Subscription to Request for Invoice",
      pendingReqOfHrdcInvoice: "Pending Request of HRDC Invoice",
      pendingHrdcInvoiceToSubmitClaim:
        "Pending HRDC invoice from taxPOD Admin to Submit Claims",
      pendingSubscriptionEndToSubmitClaim:
        "Pending End of Date of Subscription to Submit Claim",
      pendingClaimSubmission: "Pending Claim Submission",
      claimsSubmitted: "Claims Submitted, Pending HRDC to Approve Claims",
      claimsApproved: "Claims Approved, Pending HRDC to Release Funds",
    },
    pendingRefunds: {
      pendingCnReq: "taxPOD Admin - Pending CN request",
      pendingCnApproval: "taxPOD Admin - Pending CN approval",
      pendingEmailSubmissionForRefund:
        "taxPOD Admin - Pending email submission for refund",
      pendingEmailApprovalForRefund:
        "taxPOD Admin - Pending email approval for refund",
      pendingRefund:
        "Finance - Pending Refund (taxPOD Admin have not receive payment advice)",
      failedRefund:
        "Failed Refund due to client banking details error, pending second refund",
      pendingEmailPAtoClient:
        "taxPOD Admin - Pending email payment advice to client",
    },
  }),
  created() {
    //
  },
  methods: {
    isInArray(key) {
      return this.notReadyReportData.includes(key);
    },
    getBackgroundColor(key) {
      return this.isInArray(key) ? { backgroundColor: "#f3b8b5" } : "";
    },
    generateReport() {
      this.$emit("generateReport", this.generateReportData);
    },
    downloadReport() {
      let sumData = {
        A: this.ongoingApplicationSum,
        B: this.pendingRefundSum,
        C: this.generateReportData.pendingAccountEnrollment,
      };

      this.generateReportData.generalInfo.transFrHrdcToYyc =
        this.transFrHrdcToYyc ?? 0;
      this.generateReportData.ongoingApplications.pendingReqOfHrdcInvoice =
        this.pendingReqOfHrdcInvoice;

      this.$emit("downloadReport", this.generateReportData, sumData);
    },
  },
};
</script>

<style scoped>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .rv-datepicker__trigger {
  border: 1px solid var(--app-border-strong);
  background: var(--app-card);
}

.dark-mode .rv-datepicker__trigger:hover {
  border-color: var(--app-border-strong);
}

.dark-mode .rv-datepicker__icon {
  color: var(--app-text-faint);
}

.dark-mode .rv-datepicker__text {
  color: var(--app-text);
}

.dark-mode .rv-datepicker__placeholder {
  color: var(--app-text-faint);
}

.dark-mode .rv-gen-report__download-btn {
  background: var(--app-card);
}

.dark-mode .rv-gen-report__download-btn:hover {
  background: var(--app-accent-soft);
}

.dark-mode .rv-gen-report__summary-header {
  border-bottom: 1px solid var(--app-border);
}

.dark-mode .rv-gen-report__section {
  background: var(--app-elevated);
}

.dark-mode .rv-gen-report__section-body {
  background: var(--app-card);
}

.dark-mode .rv-gen-table thead tr {
  background: var(--app-surface);
}

.dark-mode .rv-gen-table th {
  color: var(--app-text-muted);
  border-bottom: 2px solid var(--app-border);
}

.dark-mode .rv-gen-table td {
  color: var(--app-text);
  border-bottom: 1px solid var(--app-border);
}

.dark-mode .rv-gen-table__num {
  color: var(--app-text-faint);
}

.dark-mode .rv-gen-table__sub-item {
  color: var(--app-text-muted);
}

.dark-mode .rv-gen-table__row--bold td {
  color: var(--app-text) !important;
  background: var(--app-surface);
}

.dark-mode .rv-gen-table__input {
  border: 1px solid var(--app-border-strong);
}

/* >>> end auto-dark <<< */

/* ============================================
   PAGE LAYOUT
   ============================================ */
.rv-gen-report {
  font-family: 'Lato', sans-serif;
  padding: 24px 28px;
}

/* ============================================
   CONTROLS (Date + Generate)
   ============================================ */
.rv-gen-report__controls {
  margin-bottom: 24px;
}

.rv-gen-report__date-row {
  display: flex;
  align-items: flex-end;
  gap: 16px;
  flex-wrap: wrap;
}

.rv-gen-report__date-col {
  flex: 0 0 340px;
  max-width: 340px;
}

.rv-gen-report__action-col {
  display: flex;
  align-items: flex-end;
  padding-bottom: 2px;
}

.rv-gen-report__label {
  display: block;
  font-size: 0.78rem;
  font-weight: 600;
  color: #3a3f47;
  margin-bottom: 6px;
}

.dark-mode .rv-gen-report__label {
  color: var(--app-text-muted);
}

.rv-gen-report__error {
  color: #dc2626;
  font-size: 0.75rem;
  margin-top: 4px;
  padding-left: 4px;
}

/* ============================================
   DATEPICKER TRIGGER
   ============================================ */
.rv-datepicker__trigger {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 42px;
  padding: 6px 12px;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  transition: all 0.15s ease;
  font-family: 'Lato', sans-serif;
  font-size: 0.85rem;
}

.rv-datepicker__trigger:hover {
  border-color: #94a3b8;
}

.rv-datepicker__trigger--error {
  border-color: #ef4444;
}

.rv-datepicker__icon {
  width: 18px;
  height: 18px;
  color: #94a3b8;
  flex-shrink: 0;
}

.rv-datepicker__text {
  color: #1e293b;
  font-weight: 600;
}

.rv-datepicker__placeholder {
  color: #94a3b8;
}

/* ============================================
   BUTTONS
   ============================================ */
.rv-gen-report__generate-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 24px;
  background: linear-gradient(135deg, #0ea5e9, #38bdf8);
  color: #fff;
  border: none;
  border-radius: 10px;
  font-family: 'Lato', sans-serif;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(14, 165, 233, 0.25);
}

.rv-gen-report__generate-btn:hover {
  background: linear-gradient(135deg, #0284c7, #0ea5e9);
  box-shadow: 0 4px 14px rgba(14, 165, 233, 0.35);
  transform: translateY(-1px);
}

.rv-gen-report__download-btn {
  display: inline-flex;
  align-items: center;
  padding: 8px 18px;
  background: #fff;
  color: #0ea5e9;
  border: 1.5px solid #0ea5e9;
  border-radius: 10px;
  font-family: 'Lato', sans-serif;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.rv-gen-report__download-btn:hover {
  background: #f0f9ff;
  border-color: #0284c7;
}

/* ============================================
   SUMMARY
   ============================================ */
.rv-gen-report__summary {
  margin-top: 8px;
}

.rv-gen-report__summary-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 0;
  margin-bottom: 16px;
  border-bottom: 1px solid #e2e8f0;
  flex-wrap: wrap;
  gap: 12px;
}

.rv-gen-report__summary-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: #3a3f47;
  margin: 0 0 4px 0;
}

.dark-mode .rv-gen-report__summary-title {
  color: var(--app-text);
}

.rv-gen-report__summary-week {
  font-size: 0.82rem;
  font-weight: 600;
  color: #0ea5e9;
  letter-spacing: 1px;
}

.rv-gen-report__todate-label {
  font-size: 0.82rem;
  font-weight: 700;
  color: #3a3f47;
  padding: 12px 0 8px;
  letter-spacing: 0.5px;
}

.dark-mode .rv-gen-report__todate-label {
  color: var(--app-text);
}

/* ============================================
   SECTION CARDS
   ============================================ */
.rv-gen-report__section {
  background: #eaecef;
  border-radius: 14px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.03);
  overflow: hidden;
  margin-bottom: 16px;
  transition: box-shadow 0.3s ease;
}

.rv-gen-report__section:hover {
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.06);
}

.rv-gen-report__section-header {
  background: linear-gradient(135deg, #0ea5e9, #38bdf8);
  padding: 12px 20px;
  display: flex;
  align-items: center;
  color: #fff;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 2px;
  text-transform: uppercase;
}

.rv-gen-report__section-body {
  padding: 18px 20px;
  background: #fff;
}

/* ============================================
   TABLE
   ============================================ */
.rv-gen-table {
  width: 100%;
  border-collapse: collapse;
  font-family: 'Lato', sans-serif;
}

.rv-gen-table thead tr {
  background: #f8fafc;
}

.rv-gen-table th {
  font-size: 0.72rem;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 10px 14px;
  border-bottom: 2px solid #e2e8f0;
  text-align: left;
}

.rv-gen-table td {
  font-size: 0.82rem;
  color: #334155;
  padding: 10px 14px;
  border-bottom: 1px solid #f1f5f9;
}

.rv-gen-table__num {
  color: #94a3b8;
  font-weight: 600;
  text-align: center;
}

.rv-gen-table__qty {
  font-weight: 600;
  text-align: center;
}

.rv-gen-table__sub-item {
  padding-left: 28px !important;
  color: #64748b;
  font-size: 0.78rem;
}

.rv-gen-table__row--bold td {
  font-weight: 700 !important;
  color: #1e293b !important;
  background: #f8fafc;
}

.rv-gen-table__input {
  width: 80px;
  padding: 4px 8px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-family: 'Lato', sans-serif;
  font-size: 0.82rem;
  font-weight: 600;
  text-align: center;
  outline: none;
  transition: border-color 0.15s ease;
}

.rv-gen-table__input:focus {
  border-color: #0ea5e9;
  box-shadow: 0 0 0 2px rgba(14, 165, 233, 0.15);
}

/* Chrome, Safari, Edge, Opera */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type="number"] {
  -moz-appearance: textfield;
}

/* ============================================
   RESPONSIVE
   ============================================ */
@media (max-width: 768px) {
  .rv-gen-report {
    padding: 16px 14px;
  }

  .rv-gen-report__date-col {
    flex: 1 1 100%;
    max-width: 100%;
  }

  .rv-gen-report__date-row {
    flex-direction: column;
    align-items: stretch;
  }

  .rv-gen-report__summary-header {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>

<style>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .v-picker__body {
  background: var(--app-card) !important;
}

.dark-mode .v-date-picker-header {
  background: var(--app-card) !important;
}

.dark-mode .v-date-picker-header__value button {
  color: var(--app-text) !important;
}

.dark-mode .v-date-picker-header .v-btn {
  color: var(--app-text-muted) !important;
}

.dark-mode .v-date-picker-table thead th {
  color: var(--app-text-faint) !important;
}

.dark-mode .v-date-picker-table .v-btn {
  color: var(--app-text) !important;
}

.dark-mode .v-date-picker-table .v-btn:hover {
  background: var(--app-accent-soft) !important;
}

.dark-mode .v-picker__actions {
  background: var(--app-card) !important;
  border-top: 1px solid var(--app-border) !important;
}

/* >>> end auto-dark <<< */

/* Date picker popup — Tailwind theme */
.v-picker {
  border-radius: 14px !important;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12) !important;
  border: none !important;
  overflow: hidden;
  font-family: "Lato", sans-serif !important;
}

.v-picker .v-picker__body { border: none !important; }

/* The unscoped `.v-menu__content …` block that used to sit here is gone.
   It was global — unscoped CSS is injected once and never removed, so a rule
   written for THIS page's dropdown repainted every menu in the app (with
   !important, and with hard-coded light hex) for the rest of the session. That
   is what made the same v-select look different depending on which page you had
   visited. Menus are defined ONCE, in tokens, in src/plugins/theme-glass.css §3
   — panel, list, item radius, hover, active, typography and the state overlay.
   A page that genuinely needs its own menu styling should give that v-menu a
   `content-class` and key off it, the way .bdt-menu and .tb-view-menu do. */
.v-picker__body { background: #fff !important; }

.v-date-picker-header { padding: 12px 16px 8px !important; background: #fff !important; }
.v-date-picker-header__value button { font-family: "Lato", sans-serif !important; font-weight: 700 !important; font-size: 0.95rem !important; color: #1e293b !important; }
.v-date-picker-header .v-btn { color: #64748b !important; }

.v-date-picker-table thead th { font-family: "Lato", sans-serif !important; font-size: 0.72rem !important; font-weight: 600 !important; color: #94a3b8 !important; padding: 8px 0 !important; }
.v-date-picker-table .v-btn { font-family: "Lato", sans-serif !important; font-size: 0.82rem !important; font-weight: 500 !important; color: #334155 !important; }
.v-date-picker-table .v-btn:hover { background: #f0f9ff !important; }
.v-date-picker-table .v-btn--active { background: #0ea5e9 !important; color: #fff !important; font-weight: 700 !important; }
.v-date-picker-table .v-btn--active:hover { background: #0284c7 !important; }
.v-date-picker-table .v-btn--outlined { border-color: #0ea5e9 !important; color: #0ea5e9 !important; }

.v-picker__actions { padding: 8px 16px 14px !important; background: #fff !important; border-top: 1px solid #f1f5f9 !important; }
.v-picker__actions .v-btn { font-family: "Lato", sans-serif !important; font-weight: 600 !important; font-size: 0.82rem !important; text-transform: none !important; border-radius: 8px !important; padding: 0 16px !important; }

.v-date-picker-years li, .v-date-picker-months .v-btn { font-family: "Lato", sans-serif !important; }
</style>
